import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import {
  UserIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import customFetch from '@utils/customFetch';

// Muss zur Validierung im Backend passen (MANAGED_PASSWORD_MIN_LENGTH).
const PASSWORD_MIN_LENGTH = 6;

const INPUT_CLASSES =
  'block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6';

const NewUserSlideIn = ({ showSlideIn, setShowSlideIn, onSubmit, companyContext = false }) => {
  // Step: 'email' | 'existing' | 'new'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [existingUser, setExistingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Aktivierungs-E-Mail an: der Benutzer setzt sein Passwort selbst.
  // Aus: der Admin kann hier direkt eines vergeben (optional).
  const [sendMail, setSendMail] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const resetState = () => {
    setStep('email');
    setEmail('');
    setChecking(false);
    setExistingUser(null);
    setSubmitting(false);
    setSendMail(true);
    setPassword('');
    setPasswordConfirm('');
    setShowPassword(false);
    setPasswordError('');
  };

  const handleClose = () => {
    setShowSlideIn(false);
    resetState();
  };

  const handleEmailCheck = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setChecking(true);
    try {
      const { data } = await customFetch.get(
        `/users/admin/check-email?email=${encodeURIComponent(email.trim())}`
      );
      if (data.exists) {
        setExistingUser(data.user);
        setStep('existing');
      } else {
        setStep('new');
      }
    } catch (error) {
      // Fallback: if check fails, show new user form
      setStep('new');
    } finally {
      setChecking(false);
    }
  };

  const handleAddExisting = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.set('userId', existingUser._id);
      formData.set('email', existingUser.email);
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  // Passwort ist optional: leer lassen erzeugt weiterhin ein rein verwaltetes
  // Profil ohne Login. Ist eines gesetzt, muss es zur Bestätigung passen.
  const validatePassword = () => {
    if (sendMail || !password) return '';
    if (password.length < PASSWORD_MIN_LENGTH) {
      return `Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein`;
    }
    if (password !== passwordConfirm) {
      return 'Die Passwörter stimmen nicht überein';
    }
    return '';
  };

  const handleCreateNew = async (e) => {
    e.preventDefault();

    const error = validatePassword();
    setPasswordError(error);
    if (error) return;

    setSubmitting(true);
    try {
      const formData = new FormData(e.target);
      formData.set('email', email.trim());
      if (!sendMail && password) {
        formData.set('password', password);
      } else {
        formData.delete('password');
      }
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={showSlideIn} as={Fragment} afterLeave={resetState}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
                      <div className="px-4 sm:px-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                            {step === 'email' && 'Benutzer hinzufügen'}
                            {step === 'existing' && 'Benutzer gefunden'}
                            {step === 'new' && 'Neuen Benutzer erstellen'}
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500"
                              onClick={handleClose}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="relative mt-6 flex-1 px-4 sm:px-6">
                        {/* Step 1: Email Check */}
                        {step === 'email' && (
                          <form onSubmit={handleEmailCheck}>
                            <div className="space-y-6 pt-6">
                              <p className="text-sm text-gray-500">
                                Gib die E-Mail-Adresse ein, um zu prüfen ob der
                                Benutzer bereits existiert.
                              </p>
                              <div>
                                <label
                                  htmlFor="emailCheck"
                                  className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                  E-Mail-Adresse
                                </label>
                                <div className="mt-2">
                                  <input
                                    type="email"
                                    id="emailCheck"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="beispiel@firma.ch"
                                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                  />
                                </div>
                              </div>
                              <button
                                type="submit"
                                disabled={checking}
                                className="w-full rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                              >
                                {checking ? 'Prüfe...' : 'Weiter'}
                              </button>
                            </div>
                          </form>
                        )}

                        {/* Step 2a: Existing User Found */}
                        {step === 'existing' && existingUser && (
                          <div className="space-y-6 pt-6">
                            <div className="rounded-lg bg-blue-50 p-4">
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                                  <UserIcon className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {existingUser.firstName}{' '}
                                    {existingUser.lastName}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {existingUser.email}
                                  </p>
                                  {existingUser.title && (
                                    <p className="text-xs text-gray-400">
                                      {existingUser.title}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {existingUser.hasCompany ? (
                              <div className="rounded-lg bg-yellow-50 p-4">
                                <div className="flex gap-3">
                                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                                  <p className="text-sm text-yellow-700">
                                    Dieser Benutzer gehört bereits zu einer
                                    Firma und kann nicht erneut hinzugefügt
                                    werden.
                                  </p>
                                </div>
                              </div>
                            ) : companyContext ? (
                              <div>
                                <p className="text-sm text-gray-600 mb-4">
                                  Dieser Benutzer existiert bereits. Möchtest du
                                  ihn zur Firma hinzufügen?
                                </p>
                                <button
                                  type="button"
                                  onClick={handleAddExisting}
                                  disabled={submitting}
                                  className="w-full rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                  {submitting
                                    ? 'Wird hinzugefügt...'
                                    : 'Zur Firma hinzufügen'}
                                </button>
                              </div>
                            ) : (
                              <div className="rounded-lg bg-yellow-50 p-4">
                                <div className="flex gap-3">
                                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                                  <p className="text-sm text-yellow-700">
                                    Ein Benutzer mit dieser E-Mail existiert
                                    bereits.
                                  </p>
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={resetState}
                              className="w-full rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                            >
                              Andere E-Mail prüfen
                            </button>
                          </div>
                        )}

                        {/* Step 2b: New User Form */}
                        {step === 'new' && (
                          <form id="newUserForm" onSubmit={handleCreateNew}>
                            <div className="space-y-6 pt-6">
                              <div className="rounded-lg bg-gray-50 p-3">
                                <p className="text-sm text-gray-600">
                                  Kein Benutzer mit{' '}
                                  <span className="font-medium">
                                    {email}
                                  </span>{' '}
                                  gefunden. Erstelle einen neuen Benutzer.
                                </p>
                              </div>

                              <div>
                                <label
                                  htmlFor="firstName"
                                  className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                  Vorname
                                </label>
                                <div className="mt-2">
                                  <input
                                    type="text"
                                    name="firstName"
                                    id="firstName"
                                    required
                                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                  />
                                </div>
                              </div>

                              <div>
                                <label
                                  htmlFor="lastName"
                                  className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                  Nachname
                                </label>
                                <div className="mt-2">
                                  <input
                                    type="text"
                                    name="lastName"
                                    id="lastName"
                                    required
                                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                  />
                                </div>
                              </div>

                              <div>
                                <label
                                  htmlFor="emailDisplay"
                                  className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                  E-Mail
                                </label>
                                <div className="mt-2">
                                  <input
                                    type="email"
                                    id="emailDisplay"
                                    value={email}
                                    readOnly
                                    className="block w-full rounded-md border-0 px-3 py-1.5 bg-gray-50 text-gray-500 shadow-xs ring-1 ring-inset ring-gray-200 sm:text-sm sm:leading-6 cursor-not-allowed"
                                  />
                                </div>
                              </div>

                              <div>
                                <div className="relative flex items-start">
                                  <div className="flex h-6 items-center">
                                    <input
                                      type="checkbox"
                                      name="sendMail"
                                      id="sendMail"
                                      checked={sendMail}
                                      onChange={(e) => {
                                        setSendMail(e.target.checked);
                                        setPasswordError('');
                                      }}
                                      className="h-4 w-4 rounded-sm border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                                    />
                                  </div>
                                  <div className="ml-3 text-sm leading-6">
                                    <label
                                      htmlFor="sendMail"
                                      className="font-medium text-gray-900"
                                    >
                                      Aktivierungs-E-Mail senden
                                    </label>
                                    <p className="text-gray-500">
                                      Der Benutzer erhält einen Link, um ein
                                      Passwort zu setzen und sich anzumelden.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Ohne Aktivierungs-E-Mail: Passwort direkt
                                  vergeben (optional) */}
                              {!sendMail && (
                                <div className="space-y-4 rounded-lg bg-gray-50 p-4">
                                  <p className="text-sm text-gray-600">
                                    Lege optional direkt ein Passwort fest — der
                                    Benutzer kann sich damit sofort anmelden.
                                    Bleiben die Felder leer, entsteht ein rein
                                    verwaltetes Profil (kein Login).
                                  </p>

                                  <div>
                                    <label
                                      htmlFor="password"
                                      className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                      Passwort
                                    </label>
                                    <div className="relative mt-2">
                                      <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        id="password"
                                        value={password}
                                        onChange={(e) => {
                                          setPassword(e.target.value);
                                          setPasswordError('');
                                        }}
                                        autoComplete="new-password"
                                        placeholder={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen`}
                                        className={`${INPUT_CLASSES} pr-10`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setShowPassword((visible) => !visible)
                                        }
                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                                        aria-label={
                                          showPassword
                                            ? 'Passwort verbergen'
                                            : 'Passwort anzeigen'
                                        }
                                      >
                                        {showPassword ? (
                                          <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                          <EyeIcon className="h-5 w-5" />
                                        )}
                                      </button>
                                    </div>
                                  </div>

                                  <div>
                                    <label
                                      htmlFor="passwordConfirm"
                                      className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                      Passwort bestätigen
                                    </label>
                                    <div className="mt-2">
                                      <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="passwordConfirm"
                                        value={passwordConfirm}
                                        onChange={(e) => {
                                          setPasswordConfirm(e.target.value);
                                          setPasswordError('');
                                        }}
                                        autoComplete="new-password"
                                        className={INPUT_CLASSES}
                                      />
                                    </div>
                                  </div>

                                  {passwordError && (
                                    <p className="text-sm text-red-600">
                                      {passwordError}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </form>
                        )}
                      </div>
                    </div>

                    {/* Footer buttons */}
                    {step === 'new' && (
                      <div className="flex shrink-0 justify-end px-4 py-4">
                        <button
                          type="button"
                          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                          onClick={resetState}
                        >
                          Zurück
                        </button>
                        <button
                          type="submit"
                          form="newUserForm"
                          className="ml-4 inline-flex justify-center rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          disabled={submitting}
                        >
                          {submitting ? 'Speichert...' : 'Erstellen'}
                        </button>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default NewUserSlideIn;
